import {
  Component, OnInit, OnDestroy, Output, EventEmitter,
  inject, HostListener, ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ChangelogVersion } from '../model/changelog.models';
import { ChangelogService } from '../services/changelog.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-changelog-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, ScrollingModule, TranslateModule],
  templateUrl: './changelog-modal.component.html',
  styleUrls: ['./changelog-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangelogModalComponent implements OnInit, OnDestroy {

  @Output() closeModal = new EventEmitter<void>();

  private changelogService = inject(ChangelogService);
  private searchSubject = new Subject<string>();
  private searchSub!: Subscription;

  // Signals del servicio
  status = this.changelogService.status;
  error = this.changelogService.error;
  filteredVersions = this.changelogService.filteredVersions;

  // Estado local
  searchQuery = '';
  selectedCategory: string | null = null;
  availableCategories: string[] = [];

  // Umbral para activar virtual scrolling
  readonly VIRTUAL_SCROLL_THRESHOLD = 20;

  // Colores por categoría
  categoryColors: Record<string, string> = {
    'Added':      '#10b981',
    'Changed':    '#3b82f6',
    'Fixed':      '#f59e0b',
    'Removed':    '#ef4444',
    'Security':   '#8b5cf6',
    'Deprecated': '#eab308'
  };

  // Iconos por categoría
  categoryIcons: Record<string, string> = {
    'Added':      'add-circle-outline',
    'Changed':    'swap-horizontal-outline',
    'Fixed':      'build-outline',
    'Removed':    'trash-outline',
    'Security':   'shield-checkmark-outline',
    'Deprecated': 'warning-outline'
  };

  ngOnInit(): void {
    if (this.status() !== 'success') {
      this.changelogService.loadChangelog();
    }

    this.searchSub = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(query => {
      this.changelogService.searchChanges(query);
    });

    this.availableCategories = this.changelogService.getAvailableCategories();
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.changelogService.clearFilters();
  }

  /** ¿Usar virtual scrolling? */
  get useVirtualScroll(): boolean {
    return this.filteredVersions().length > this.VIRTUAL_SCROLL_THRESHOLD;
  }

  @HostListener('document:keydown.escape')
  onEscPress(): void {
    this.close();
  }

  close(): void {
    this.closeModal.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-backdrop')) {
      this.close();
    }
  }

  onSearchInput(query: string): void {
    this.searchQuery = query;
    this.searchSubject.next(query);
  }

  onCategoryChange(category: string | null): void {
    this.selectedCategory = category;
    this.changelogService.filterByCategory(category);
  }

  clearFilters(): void {
    this.searchQuery = '';
    this.selectedCategory = null;
    this.changelogService.clearFilters();
  }

  get hasActiveFilters(): boolean {
    return this.searchQuery.length > 0 || this.selectedCategory !== null;
  }

  getCategories(version: ChangelogVersion): string[] {
    return Object.keys(version.categories);
  }

  getCategoryColor(category: string): string {
    return this.categoryColors[category] || '#6b7280';
  }

  getCategoryIcon(category: string): string {
    return this.categoryIcons[category] || 'ellipse-outline';
  }

  getTotalChanges(version: ChangelogVersion): number {
    return Object.values(version.categories)
      .reduce((sum, entries) => sum + entries.length, 0);
  }

  /** Índice para trackBy en virtual scroll */
  trackByVersion(index: number, version: ChangelogVersion): string {
    return version.version;
  }

  /** ¿Es la primera versión (latest)? */
  isLatest(version: ChangelogVersion): boolean {
    const versions = this.filteredVersions();
    return versions.length > 0 && versions[0].version === version.version;
  }

  /** ¿Es la última versión de la lista? */
  isLast(version: ChangelogVersion): boolean {
    const versions = this.filteredVersions();
    return versions.length > 0 && versions[versions.length - 1].version === version.version;
  }

  retry(): void {
    this.changelogService.loadChangelog();
  }
}
